import { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";

// TEMP: verbaiq-user-gate — remove once full RBAC ships. Mirrors get-client-feature-config.ts's
// own fallback: a client may still be on the deprecated combined verbaiq_config.allowed_roles
// (or send neither), so every role sees the nav item/routes until the tenant explicitly narrows
// the surface-specific list. VerbaIQ-generated actions (chat/chart/pin) are still gated
// server-side by vf-core's allowed_user_ids allowlist regardless of this fallback.
const ALL_ROLES = [
  "vf-admin",
  "vf-editor",
  "vf-viewer",
  "superadmin",
  "admin",
  "editor",
  "commentor",
  "viewer",
];

// VerbaIQ access split into two independent surfaces — the chat/copilot drawer vs the VerbaIQ
// Analytics dashboard — mirroring vf-core's assert_role_access(surface=...). A user can have one
// without the other. Mirrors vf-core's own two-stage resolution: the surface's enabled flag is a
// hard gate checked FIRST (a disabled surface refuses everyone, including a role-allowed or
// grant-holding user — there is no bypass), then — only once enabled — access is an OR of two
// independent gates: the tenant's role ceiling (verbaiq_config.verbaiq_{chat,analytics}_
// allowed_roles, falling back to the deprecated combined allowed_roles, then ALL_ROLES) and a
// per-user grant (verbaiq_{chat,analytics}_access_client_codes). vf-admin needs no special-case
// bypass: the config's allowed_roles defaults to ["vf-admin"], so it clears the role check the
// same way every other listed role does.
export const resolveVerbaiqAccess = (
  enabled: boolean,
  currentUserRole: string | undefined,
  allowedRoles: string[] | undefined,
  surfaceCodes: string[] | null | undefined,
  currentClientCode: string | undefined
): boolean => {
  if (!enabled) return false;
  const roleAllowed =
    !!currentUserRole &&
    Array.isArray(allowedRoles) &&
    allowedRoles.includes(currentUserRole);
  const grantAllowed =
    !!currentClientCode &&
    Array.isArray(surfaceCodes) &&
    surfaceCodes.includes(currentClientCode);
  return roleAllowed || grantAllowed;
};

const resolveSurfaceAllowedRoles = (
  surfaceRoles: string[] | undefined,
  combinedRoles: string[] | undefined
): string[] =>
  surfaceRoles && surfaceRoles.length
    ? surfaceRoles
    : combinedRoles && combinedRoles.length
    ? combinedRoles
    : ALL_ROLES;

// Mirrors vf-core's _resolve_config: a surface-specific enabled flag of undefined means "not yet
// migrated off the combined enabled" — fall back to that, then default to true (VerbaIQConfig's
// own default) so an unconfigured client isn't silently locked out.
const resolveSurfaceEnabled = (
  surfaceEnabled: boolean | undefined,
  combinedEnabled: boolean | undefined
): boolean => surfaceEnabled ?? combinedEnabled ?? true;

const useVerbaiqSurfaceAccess = (
  surfaceEnabledSelector: (state: RootState) => boolean | undefined,
  surfaceAllowedRolesSelector: (state: RootState) => string[] | undefined,
  surfaceCodesSelector: (state: RootState) => string[] | null | undefined
): boolean => {
  const combinedEnabled = useSelector(
    (state: RootState) => state?.auth?.client?.verbaiq_config?.enabled
  );
  const surfaceEnabledRaw = useSelector(surfaceEnabledSelector);
  const enabled = resolveSurfaceEnabled(surfaceEnabledRaw, combinedEnabled);
  const currentUserRole = useSelector((state: RootState) => state?.auth?.user?.role);
  const combinedRoles = useSelector(
    (state: RootState) => state?.auth?.client?.verbaiq_config?.allowed_roles
  );
  const surfaceRoles = useSelector(surfaceAllowedRolesSelector);
  const allowedRoles = resolveSurfaceAllowedRoles(surfaceRoles, combinedRoles);
  const surfaceCodes = useSelector(surfaceCodesSelector);
  const currentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  return resolveVerbaiqAccess(
    enabled,
    currentUserRole,
    allowedRoles,
    surfaceCodes,
    currentClientCode
  );
};

export const useHasVerbaiqChatAccess = (): boolean =>
  useVerbaiqSurfaceAccess(
    (state) => state?.auth?.client?.verbaiq_config?.verbaiq_chat_enabled,
    (state) => state?.auth?.client?.verbaiq_config?.verbaiq_chat_allowed_roles,
    (state) => state?.auth?.user?.verbaiq_chat_access_client_codes
  );

export const useHasVerbaiqAnalyticsAccess = (): boolean =>
  useVerbaiqSurfaceAccess(
    (state) => state?.auth?.client?.verbaiq_config?.verbaiq_analytics_enabled,
    (state) => state?.auth?.client?.verbaiq_config?.verbaiq_analytics_allowed_roles,
    (state) => state?.auth?.user?.verbaiq_analytics_access_client_codes
  );

export const useVerbaiqAccessChecker = (): (() => boolean) => {
  const combinedEnabled = useSelector(
    (state: RootState) => state?.auth?.client?.verbaiq_config?.enabled
  );
  const chatEnabledRaw = useSelector(
    (state: RootState) => state?.auth?.client?.verbaiq_config?.verbaiq_chat_enabled
  );
  const enabled = resolveSurfaceEnabled(chatEnabledRaw, combinedEnabled);
  const currentUserRole = useSelector((state: RootState) => state?.auth?.user?.role);
  const combinedRoles = useSelector(
    (state: RootState) => state?.auth?.client?.verbaiq_config?.allowed_roles
  );
  const chatRoles = useSelector(
    (state: RootState) => state?.auth?.client?.verbaiq_config?.verbaiq_chat_allowed_roles
  );
  const allowedRoles = resolveSurfaceAllowedRoles(chatRoles, combinedRoles);
  const chatCodes = useSelector(
    (state: RootState) => state?.auth?.user?.verbaiq_chat_access_client_codes
  );
  const currentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  return useCallback(
    () =>
      resolveVerbaiqAccess(enabled, currentUserRole, allowedRoles, chatCodes, currentClientCode),
    [enabled, currentUserRole, allowedRoles, chatCodes, currentClientCode]
  );
};
